import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Badge } from '../ui/badge';
import { ShieldCheck, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { e2eToolApi } from '../../lib/toolsApi';

const DEFAULT_P01_MODES = ['BOTH', 'ALT', 'LOW', 'NIBBLE'];

const PROFILE_DEFAULTS = {
  '1': { data: '00 00 00 00 00 00 00 00', headerLen: 2, minLen: 2 },
  '2': { data: '00 00 00 00 00 00 00 00', headerLen: 2, minLen: 2 },
  '4': { data: '00 00 00 00 00 00 00 00 00 00 00 00', headerLen: 12, minLen: 12 },
  '5': { data: '00 00 00 00 00 00 00 00', headerLen: 3, minLen: 3 },
};

const PROFILE_LABELS = {
  '1': 'Profile 1 — CRC8, 2-byte header (Counter + Data ID nibble)',
  '2': 'Profile 2 — CRC8H2F, 2-byte header (rotating Data ID list)',
  '4': 'Profile 4 — CRC32P4, 12-byte header (Length + Counter + Data ID)',
  '5': 'Profile 5 — CRC16, 3-byte header (implicit Data ID)',
};

function parseHexField(input, maxVal, label) {
  const cleaned = (input || '').trim().replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) throw new Error(`${label} must be a hex value.`);
  const val = parseInt(cleaned, 16);
  if (val > maxVal) throw new Error(`${label} exceeds maximum of 0x${maxVal.toString(16).toUpperCase()}.`);
  return val;
}

const E2EProfileCalculator = () => {
  const [profile, setProfile] = useState('1');
  const [action, setAction] = useState('protect');
  const [dataInput, setDataInput] = useState(PROFILE_DEFAULTS['1'].data);

  // Profile 1
  const [dataIdP1, setDataIdP1] = useState('0123');
  const [modeP1, setModeP1] = useState('BOTH');
  const [counterP1, setCounterP1] = useState(0);

  // Profile 2
  const [dataIdListInput, setDataIdListInput] = useState(
    Array.from({ length: 16 }, (_, i) => i.toString(16).padStart(2, '0')).join(' ')
  );
  const [counterP2, setCounterP2] = useState(0);

  // Profile 4
  const [dataIdP4, setDataIdP4] = useState('0A0B0C0D');
  const [counterP4, setCounterP4] = useState(0);

  // Profile 5
  const [dataIdP5, setDataIdP5] = useState('1234');
  const [counterP5, setCounterP5] = useState(0);

  const [incrementCounter, setIncrementCounter] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [p01Modes, setP01Modes] = useState(DEFAULT_P01_MODES);

  useEffect(() => {
    e2eToolApi.getProfile1Modes().then(setP01Modes).catch(() => {});
  }, []);

  const handleProfileChange = (p) => {
    setProfile(p);
    setDataInput(PROFILE_DEFAULTS[p].data);
    setResult(null);
    setError(null);
  };

  const calculate = async () => {
    setError(null);
    setResult(null);
    try {
      let opts;

      if (profile === '1') {
        const dataId = parseHexField(dataIdP1, 0xffff, 'Data ID');
        opts = { dataId, mode: modeP1, counter: counterP1, incrementCounter };
      } else if (profile === '2') {
        opts = { dataIdList: dataIdListInput, counter: counterP2, incrementCounter };
      } else if (profile === '4') {
        const dataId = parseHexField(dataIdP4, 0xffffffff, 'Data ID');
        opts = { dataId, counter: counterP4, incrementCounter };
      } else if (profile === '5') {
        const dataId = parseHexField(dataIdP5, 0xffff, 'Data ID');
        opts = { dataId, counter: counterP5, incrementCounter };
      }

      const out = action === 'protect'
        ? await e2eToolApi.protect(profile, dataInput, opts)
        : await e2eToolApi.check(profile, dataInput, opts);

      setResult({ profile, action, out });
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-autoxium-blue" />
            E2E Profile Calculator
          </CardTitle>
          <CardDescription>
            Compute (Protect) or verify (Check) the CRC and Counter fields for AUTOSAR E2E Profiles 1, 2, 4, and 5.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Profile</Label>
              <Select value={profile} onValueChange={handleProfileChange}>
                <SelectTrigger data-testid="e2e-profile-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Profile 1</SelectItem>
                  <SelectItem value="2">Profile 2</SelectItem>
                  <SelectItem value="4">Profile 4</SelectItem>
                  <SelectItem value="5">Profile 5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Action</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="e2e-action-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="protect">Protect (compute CRC + Counter)</SelectItem>
                  <SelectItem value="check">Check (verify existing CRC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="font-mono text-xs text-muted-foreground">{PROFILE_LABELS[profile]}</p>

          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Data Payload (hex bytes, starting with the CRC/header field{profile === '2' ? '' : 's'})
            </Label>
            <Textarea
              value={dataInput}
              onChange={(e) => setDataInput(e.target.value)}
              className="font-mono text-sm min-h-[80px]"
              data-testid="e2e-data-input"
            />
          </div>

          {profile === '1' && (
            <div className="grid sm:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-md">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Data ID (hex)</Label>
                <Input value={dataIdP1} onChange={(e) => setDataIdP1(e.target.value)} className="font-mono" data-testid="e2e-p1-dataid" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Data ID Mode</Label>
                <Select value={modeP1} onValueChange={setModeP1}>
                  <SelectTrigger data-testid="e2e-p1-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {p01Modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Counter (0-14)</Label>
                <Input type="number" min={0} max={14} value={counterP1} onChange={(e) => setCounterP1(Number(e.target.value))} className="font-mono" data-testid="e2e-p1-counter" />
              </div>
            </div>
          )}

          {profile === '2' && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Data ID List (16 bytes, hex)</Label>
                <Textarea value={dataIdListInput} onChange={(e) => setDataIdListInput(e.target.value)} className="font-mono text-sm min-h-[60px]" data-testid="e2e-p2-idlist" />
              </div>
              <div className="space-y-2 w-1/3">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Counter (0-15)</Label>
                <Input type="number" min={0} max={15} value={counterP2} onChange={(e) => setCounterP2(Number(e.target.value))} className="font-mono" data-testid="e2e-p2-counter" />
              </div>
            </div>
          )}

          {profile === '4' && (
            <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-md">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Data ID (32-bit hex)</Label>
                <Input value={dataIdP4} onChange={(e) => setDataIdP4(e.target.value)} className="font-mono" data-testid="e2e-p4-dataid" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Counter (16-bit)</Label>
                <Input type="number" min={0} max={65535} value={counterP4} onChange={(e) => setCounterP4(Number(e.target.value))} className="font-mono" data-testid="e2e-p4-counter" />
              </div>
            </div>
          )}

          {profile === '5' && (
            <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-md">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Data ID (16-bit hex)</Label>
                <Input value={dataIdP5} onChange={(e) => setDataIdP5(e.target.value)} className="font-mono" data-testid="e2e-p5-dataid" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Counter (0-255)</Label>
                <Input type="number" min={0} max={255} value={counterP5} onChange={(e) => setCounterP5(Number(e.target.value))} className="font-mono" data-testid="e2e-p5-counter" />
              </div>
            </div>
          )}

          {action === 'protect' && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={incrementCounter} onChange={(e) => setIncrementCounter(e.target.checked)} data-testid="e2e-increment-checkbox" />
              Increment counter (from the value above) before computing
            </label>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="e2e-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <Button onClick={calculate} className="btn-premium w-full" data-testid="e2e-calculate-btn">
            Calculate
          </Button>
        </CardContent>
      </Card>

      <Card className="technical-card lg:col-span-2 h-fit">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Result</CardTitle>
        </CardHeader>
        <CardContent>
          {!result ? (
            <p className="text-sm text-muted-foreground">Configure the profile and click Calculate.</p>
          ) : result.action === 'protect' ? (
            <div className="space-y-4" data-testid="e2e-result">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Protected Data</p>
                <p className="font-mono text-sm break-all">
                  {result.out.headerHex}
                  {result.out.payloadHex && (
                    <span className="text-muted-foreground"> {result.out.payloadHex}</span>
                  )}
                </p>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Badge variant="outline" className="font-mono text-[10px]">header</Badge> vs payload bytes shown separately
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-structure">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">CRC</p>
                  <p className="font-mono text-lg font-bold text-autoxium-blue">{result.out.crcHex}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Counter</p>
                  <p className="font-mono text-lg font-bold">{result.out.counter}</p>
                </div>
              </div>
              {result.out.selectedDataIdHex !== undefined && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Selected Data ID (from list)</p>
                  <p className="font-mono text-sm">{result.out.selectedDataIdHex}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4" data-testid="e2e-result">
              <div className="flex items-center gap-2">
                {result.out.valid ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="font-chivo font-bold uppercase text-emerald-600">Valid</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-destructive" />
                    <span className="font-chivo font-bold uppercase text-destructive">Invalid</span>
                  </>
                )}
              </div>
              {result.out.reason && <p className="text-sm text-muted-foreground">{result.out.reason}</p>}
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-structure">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Expected CRC</p>
                  <p className="font-mono text-sm">{result.out.expectedCrcHex || '—'}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Actual CRC</p>
                  <p className="font-mono text-sm">{result.out.actualCrcHex || '—'}</p>
                </div>
              </div>
              {result.out.counter !== undefined && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Counter</p>
                  <p className="font-mono text-sm">{result.out.counter}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default E2EProfileCalculator;
